import { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import clsx from 'clsx';
import { useCancelSubscription } from 'services/payments/subscriptions/hooks';

import { Button } from 'components/_basic/Button/Button';
import { CloseButton } from 'components/_basic/CloseButton/CloseButton';
import { ModalComponent } from 'components/_basic/ModalComponent/ModalComponent';

import { formatDate } from 'helpers/formatDates';
import { getToastSuccessContent } from 'helpers/getCancelSubscriptionToast';
import ToastService from 'helpers/ToastService';

import { ICancelSubscriptionModalProps as IProps } from './types';

import style from './CancelSubscriptionModal.module.scss';

export const CancelSubscriptionModal: FC<IProps> = ({ isOpened, handleClose, userSubscription }) => {
  const { t } = useTranslation();

  const cancelSubscriptionMutation = useCancelSubscription();
  const formattedDate = formatDate(userSubscription.currentSubscription.endDate);
  const title = t('cancelSubscription');
  const { title: toastTitle, text: toastText } = getToastSuccessContent(userSubscription);

  const cancelSubscriptionHandler = async () => {
    try {
      await cancelSubscriptionMutation.mutateAsync();

      ToastService.showSuccess(toastText, toastTitle);
      handleClose();
    } catch (error) {
      if (error instanceof Error) {
        ToastService.showError(error.message);
      }
    }
  };

  return (
    <ModalComponent open={isOpened} onClose={handleClose}>
      <Box className={style.modalWrapper}>
        <Typography variant="h3" className={style.title}>
          {title}
          <CloseButton onClick={handleClose} />
        </Typography>
        <Box className={style.content}>
          <Typography component="p" variant="body2">
            {t('sureToEndSubscription')}
          </Typography>
          <Typography component="p" variant="body2">
            {t('editingOfDocumentsWillBeDisabled')}
          </Typography>
          <Typography component="p" variant="body2">
            <Trans i18nKey="subscriptionEndsDate_plural" date={formattedDate}>
              Current subscription ends on{' '}
              <Box component="span" className={style.endDate}>
                {formattedDate}.
              </Box>
            </Trans>
          </Typography>
        </Box>
        <Box className={style.buttonsWrapper}>
          <Button
            variant="outlined"
            disabled={cancelSubscriptionMutation.isPending}
            isLoading={cancelSubscriptionMutation.isPending}
            onClick={handleClose}
            className={style.button}
          >
            {t('keepSubscription')}
          </Button>
          <Button
            variant="contained"
            className={clsx(style.button, style.cancelSubscription)}
            disabled={cancelSubscriptionMutation.isPending}
            isLoading={cancelSubscriptionMutation.isPending}
            onClick={cancelSubscriptionHandler}
          >
            {title}
          </Button>
        </Box>
      </Box>
    </ModalComponent>
  );
};
